// Third-party
import * as React from 'react';
import Image from 'next/image';
import { useToggle } from '@hooks';

// Utils
import { imagekitLoader } from '@utils/imagekit-loader';
import { GRAY_PIXEL_DATA_URL } from '@utils/constants';
import { appendUrlParams } from '@utils/append-url-params';
import { AccountReviewSummary, AccountReviewSummarySource } from '@gql';

// UI
import { Link } from '@components/link';
import { Typography } from '@components/typography';
import { Button } from '@components/button';
import { SocialSection } from '../listing-show/social-section';
import { Avatar } from '@components/avatar';
import { GoogleRatingsDisplay } from '@components/google-ratings-display';
import { AnalyticsRef } from '@components/analytics-ref';
import { PhoneNumberModal } from '@pages-components/listing-show/heading-and-title-bar/phone-number-modal';
import { AccountShowList } from './account-show-list';
import { ExpandableText } from '@components/expandable-text';

type Props = {
  account: {
    id: string;
    slug: string;
    avatarUrl?: string;
    heroUrl?: string;
    companyName?: string;
    title?: string;
    description?: string;
    email?: string;
    phoneNumber?: string;
    marketingWebsiteUrl?: string;
    twitterHandle?: string;
    instagramUsername?: string;
    twitterUrl?: string;
    instagramUrl?: string;
    facebookPageUrl?: string;
    youtubeUrl?: string;
    linkedinUrl?: string;
    inBusinessSince?: number;
    accountReviewSummaries: AccountReviewSummary[];
  };
};

export const AccountShow: React.FC<Props> = ({ account }) => {
  const {
    on: phoneNumberModalIsOpen,
    setOff: closePhoneNumberModal,
    setOn: openPhoneNumberModal,
  } = useToggle();

  const googleReviewSummary = account.accountReviewSummaries.find(
    (reviewSummary) =>
      reviewSummary.source === AccountReviewSummarySource.GoogleReviews
  );

  const shouldShowCTAModal =
    account.email || account.phoneNumber || account.marketingWebsiteUrl;

  const shouldShowSocialSection =
    account.facebookPageUrl ||
    account.instagramUrl ||
    account.twitterUrl ||
    account.linkedinUrl ||
    account.youtubeUrl;

  return (
    <div className="px-0 pb-8 body-container text-primary-main">
      {account.heroUrl ? (
        <div className="w-full aspect-3 sm:overflow-hidden sm:rounded-lg">
          <div className="relative sm:w-full h-full w-screen -ml-4 sm:ml-0">
            <Image
              layout="fill"
              src={account.heroUrl}
              alt="hero asset"
              placeholder="blur"
              className="object-center object-cover"
              loader={imagekitLoader}
              blurDataURL={GRAY_PIXEL_DATA_URL}
              priority={true}
            />
          </div>
        </div>
      ) : (
        <div className="h-10" />
      )}
      <div className="mx-auto px-4 sm:px-0">
        <div className="flex flex-col md:flex-row md:space-x-10 sm:pl-4">
          <div className="flex flex-col md:w-1/3 space-y-2">
            {account.avatarUrl ? (
              <div className="-mt-14 sm:-mt-20 w-24 h-24 sm:w-44 sm:h-44 z-10 overflow-hidden rounded-full shadow-lg">
                <Avatar shape="square" avatarUrl={account.avatarUrl} />
              </div>
            ) : null}

            <Typography
              component="h1"
              variant="title3"
              variantM="title3"
              variantL="title2"
              className="overflow-hidden text-ellipsis pt-2 sm:pt-6"
            >
              {account.companyName}
            </Typography>
            <Typography variant="body2" variantM="body1" component="p">
              {account.title}
            </Typography>

            {account.inBusinessSince && (
              <Typography variant="small" variantM="body2" component="p">
                In business since {account.inBusinessSince}
              </Typography>
            )}

            {shouldShowCTAModal && (
              <div className="w-full mt-4 z-40 p-4 space-y-4 -mx-4">
                <div className="flex flex-row space-x-2 pb-2">
                  {account.marketingWebsiteUrl && (
                    <Link
                      isButton={true}
                      to={appendUrlParams(account.marketingWebsiteUrl, {
                        utm_source: 'savvy',
                        utm_medium: 'website',
                      })}
                      target="_blank"
                      size="sm"
                      rel="noreferrer"
                      variant="filled"
                      color="secondary"
                    >
                      View Website
                    </Link>
                  )}
                  {account.phoneNumber && (
                    <div className="">
                      <PhoneNumberModal
                        isOpen={phoneNumberModalIsOpen}
                        closeModal={closePhoneNumberModal}
                        phoneNumber={account.phoneNumber}
                      />
                      <AnalyticsRef name="select_account_phone">
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={openPhoneNumberModal}
                          size="sm"
                        >
                          Call Property Manager
                        </Button>
                      </AnalyticsRef>
                    </div>
                  )}
                </div>

                {shouldShowSocialSection && (
                  <SocialSection
                    contacts={{
                      facebookPageUrl: account.facebookPageUrl,
                      instagramUrl: account.instagramUrl,
                      twitterUrl: account.twitterUrl,
                      linkedinUrl: account.linkedinUrl,
                      youtubeUrl: account.youtubeUrl,
                      publicEmail: account.email,
                      publicPhoneNumber: account.phoneNumber,
                    }}
                    shouldShowSocialMedia={true}
                    shouldShowWebsiteUrlAsButton={true}
                  />
                )}
              </div>
            )}

            <ExpandableText
              text={account.description || ''}
              maxLines={5}
              maxLength={250}
              className="mt-4 pr-4 text-primary-main"
              typographyVariant="body2"
              typographyVariantM="body2"
              buttonColor="primary"
              buttonVariant="text"
              buttonClassName="underline pr-8"
            />

            {googleReviewSummary && (
              <div className="pt-4">
                <GoogleRatingsDisplay reviewSummary={googleReviewSummary} />
              </div>
            )}
          </div>
          <div className="flex flex-col md:w-2/3">
            <AccountShowList accountSlug={account.slug} />
          </div>
        </div>
      </div>
    </div>
  );
};
